/**
 * Query Prefetching Utilities
 *
 * Prefetch queries for better UX.
 * Updated for Workshop v2.
 *
 * @version 3.0.0
 */

import {queryClient} from './query-client';
import {queryKeys} from './query-keys';
import {logger} from './logger/logger';

/**
 * Prefetch supported countries
 */
export async function prefetchSupportedCountries(): Promise<void> {
  try {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.countries.supported,
      queryFn: async () => {
        const { fetchSupportedCountries } = await import('@/features/country/hooks');
        return fetchSupportedCountries();
      },
      staleTime: 5 * 60 * 1000,
    });
  } catch (error) {
    logger.warn('[PREFETCH] Failed to prefetch supported countries', {
      component: 'query-prefetch',
      action: 'prefetch_supported_countries',
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Prefetch user profile when user is authenticated
 */
export async function prefetchUserProfile(userId: string): Promise<void> {
  if (!userId) return;

  try {
    await queryClient.prefetchQuery({
      queryKey: queryKeys.user.profile(userId),
      queryFn: async () => {
        const { authService } = await import('@/features/auth');
        const result = await authService.getUserProfile(userId);
        if (result.error) {
          throw result.error;
        }
        return result.data;
      },
      staleTime: 5 * 60 * 1000,
    });
  } catch (error) {
    logger.warn('[PREFETCH] Failed to prefetch user profile', {
      component: 'query-prefetch',
      action: 'prefetch_user_profile',
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Prefetch orders by email when user is authenticated
 */
export async function prefetchOrdersByEmail(email: string): Promise<void> {
  if (!email) return;
  try {
    const { ordersBySessionQueryOptions } = await import('@/features/orders');
    await queryClient.prefetchQuery(ordersBySessionQueryOptions(email));
  } catch (error) {
    logger.warn('[PREFETCH] Failed to prefetch orders by email', {
      component: 'query-prefetch',
      action: 'prefetch_orders_by_email',
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

/**
 * Prefetch products catalog
 *
 * Prefetches categories and featured products for the current country.
 * Uses country from store if available, otherwise skips prefetch.
 */
export async function prefetchProducts(): Promise<void> {
  try {
    // Get country from store (non-reactive, safe for prefetch)
    const { getCountryForAPI } = await import('@/features/country');
    const { countryCode, isReady } = getCountryForAPI();

    if (!isReady || !countryCode) {
      // Skip prefetch if country not selected yet
      return;
    }

    // Prefetch categories (no country needed)
    const { getCategories } = await import('@/features/products');
    await queryClient.prefetchQuery({
      queryKey: ['products', 'categories'],
      queryFn: getCategories,
      staleTime: 10 * 60 * 1000,
    });

    // Prefetch featured products for the country
    const { getProducts } = await import('@/features/products');
    await queryClient.prefetchQuery({
      queryKey: ['products', 'list', { countryCode, category: undefined }],
      queryFn: () => getProducts(countryCode, { limit: 12 }),
      staleTime: 5 * 60 * 1000,
    });
  } catch (error) {
    logger.warn('[PREFETCH] Failed to prefetch products', {
      component: 'query-prefetch',
      action: 'prefetch_products',
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
